import React, { forwardRef, useState, useEffect, useRef } from 'react';
import axios from 'axios';

const MessageList = forwardRef(({ 
  messages, 
  loading, 
  user, 
  formatTimestamp, 
  onDeleteMessage,
  messagesEndRef,
  onAddReaction,
  onRemoveReaction
}, ref) => {
  const [showEmojiPicker, setShowEmojiPicker] = useState(null); // null or messageId
  const [showMessageMenu, setShowMessageMenu] = useState(null); // null or messageId
  const emojiPickerRef = useRef(null);
  const messageMenuRef = useRef(null);
  const [commonEmojis, setCommonEmojis] = useState(["👍", "❤️", "😂", "🎉", "👏", "🙏", "🔥", "✅"]);
  const [leadCache, setLeadCache] = useState({});

  // Function to fetch lead data by ID
  const fetchLeadData = async (leadId) => {
    if (leadCache[leadId]) {
      return leadCache[leadId];
    }

    try {
      const token = localStorage.getItem('token');
      const config = { headers: { 'x-auth-token': token } };
      const response = await axios.get(`/api/leads/${leadId}`, config);
      
      // Cache the lead data
      setLeadCache(prev => ({
        ...prev,
        [leadId]: response.data
      }));
      
      return response.data;
    } catch (error) {
      console.error('Error fetching lead data:', error);
      
      // Check if it's a 404 Not Found error, indicating the lead has been deleted
      if (error.response && error.response.status === 404) {
        // Mark this lead as deleted in the cache
        setLeadCache(prev => ({
          ...prev,
          [leadId]: { isDeleted: true, id: leadId }
        }));
        return { isDeleted: true, id: leadId };
      }
      
      return null;
    }
  };

  // Function to format message content with lead links
  const formatMessageContent = (content) => {
    if (!content) return '';

    // Regular expression to match lead URLs
    // This pattern will match URLs like "http://yourapp.com/leads/12345" or "/leads/12345"
    const leadUrlPattern = /((?:https?:\/\/[^\/]+)?\/leads\/(\d+))/g;
    
    // If there are no matches, return the original content
    if (!content.match(leadUrlPattern)) {
      return content;
    }

    // Create the formatted content by replacing all lead URLs
    let lastIndex = 0;
    const formattedContent = [];
    let match;
    let contentCopy = content;
    
    // Create a new RegExp with the same pattern to use with exec
    const regex = new RegExp(leadUrlPattern);
    
    // Process each match
    while ((match = regex.exec(contentCopy)) !== null) {
      // Add text before the match
      if (match.index > lastIndex) {
        formattedContent.push(
          <span key={`text-${lastIndex}`}>
            {contentCopy.substring(lastIndex, match.index)}
          </span>
        );
      }
      
      // Get the lead ID from the match
      const fullUrl = match[1]; // The full URL match
      const leadId = match[2];  // The ID capture group
      const leadInfo = leadCache[leadId];
      
      // Add the lead link
      if (leadInfo) {
        // Check if the lead is marked as deleted
        if (leadInfo.isDeleted) {
          // Show deleted lead card
          formattedContent.push(
            <div 
              key={`lead-${match.index}`}
              className="inline-block mx-1 my-2"
            >
              <div className="flex items-center bg-red-50 text-red-800 rounded-lg px-3 py-2 border border-red-200 shadow-sm max-w-xs">
                {/* Lead icon with X */}
                <div className="flex-shrink-0 mr-2 h-8 w-8 bg-red-200 rounded-full flex items-center justify-center text-red-600">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                  </svg>
                </div>
                
                {/* Lead details */}
                <div className="overflow-hidden">
                  <div className="font-medium text-sm truncate">Lead #{leadId}</div>
                  <div className="text-xs text-red-600">Lead deleted</div>
                </div>
              </div>
            </div>
          );
        } else {
          // We have the lead info in cache - display a nice contact card
          formattedContent.push(
            <a 
              key={`lead-${match.index}`}
              href={`/leads/${leadId}`}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-block mx-1 my-2 no-underline"
            >
              <div className="flex items-center bg-blue-100 text-blue-900 rounded-lg px-3 py-2 border border-blue-200 hover:bg-blue-200 transition-colors shadow-sm max-w-xs">
                {/* Lead icon */}
                <div className="flex-shrink-0 mr-2 h-8 w-8 bg-blue-500 rounded-full flex items-center justify-center text-white">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clipRule="evenodd" />
                  </svg>
                </div>
                
                {/* Lead details */}
                <div className="overflow-hidden">
                  <div className="font-medium text-sm truncate">{leadInfo.name}</div>
                  <div className="text-xs text-blue-700 truncate">{leadInfo.email}</div>
                  {leadInfo.status && 
                    <div className="mt-1">
                      <span className={`inline-block px-2 py-0.5 text-xs rounded-full ${
                        leadInfo.status === 'HOT' ? 'bg-red-100 text-red-800' :
                        leadInfo.status === 'new' ? 'bg-green-100 text-green-800' :
                        leadInfo.status === 'connected' ? 'bg-blue-100 text-blue-800' :
                        leadInfo.status === 'converted' ? 'bg-purple-100 text-purple-800' :
                        'bg-gray-100 text-gray-800'
                      }`}>
                        {leadInfo.status}
                      </span>
                    </div>
                  }
                </div>
                
                {/* External link icon */}
                <svg xmlns="http://www.w3.org/2000/svg" className="ml-2 h-4 w-4 text-blue-500" viewBox="0 0 20 20" fill="currentColor">
                  <path d="M11 3a1 1 0 100 2h2.586l-6.293 6.293a1 1 0 101.414 1.414L15 6.414V9a1 1 0 102 0V4a1 1 0 00-1-1h-5z" />
                  <path d="M5 5a2 2 0 00-2 2v8a2 2 0 002 2h8a2 2 0 002-2v-3a1 1 0 10-2 0v3H5V7h3a1 1 0 000-2H5z" />
                </svg>
              </div>
            </a>
          );
        }
      } else {
        // Lead not loaded yet, show a loading placeholder card
        formattedContent.push(
          <a 
            key={`lead-${match.index}`}
            href={`/leads/${leadId}`}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-block mx-1 my-2 no-underline"
          >
            <div className="flex items-center bg-gray-100 text-gray-700 rounded-lg px-3 py-2 border border-gray-200 hover:bg-gray-200 transition-colors shadow-sm max-w-xs animate-pulse">
              {/* Lead icon */}
              <div className="flex-shrink-0 mr-2 h-8 w-8 bg-gray-400 rounded-full flex items-center justify-center text-white">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clipRule="evenodd" />
                </svg>
              </div>
              
              {/* Lead details skeleton */}
              <div className="overflow-hidden">
                <div className="font-medium text-sm">Lead #{leadId}</div>
                <div className="text-xs text-gray-500">Loading...</div>
              </div>
              
              {/* External link icon */}
              <svg xmlns="http://www.w3.org/2000/svg" className="ml-2 h-4 w-4 text-gray-400" viewBox="0 0 20 20" fill="currentColor">
                <path d="M11 3a1 1 0 100 2h2.586l-6.293 6.293a1 1 0 101.414 1.414L15 6.414V9a1 1 0 102 0V4a1 1 0 00-1-1h-5z" />
                <path d="M5 5a2 2 0 00-2 2v8a2 2 0 002 2h8a2 2 0 002-2v-3a1 1 0 10-2 0v3H5V7h3a1 1 0 000-2H5z" />
              </svg>
            </div>
          </a>
        );
        
        // Trigger a fetch of the lead data for next render
        fetchLeadData(leadId);
      }
      
      // Update the last index
      lastIndex = match.index + fullUrl.length;
    }
    
    // Add any remaining text after the last match
    if (lastIndex < contentCopy.length) {
      formattedContent.push(
        <span key={`text-${lastIndex}`}>
          {contentCopy.substring(lastIndex)}
        </span>
      );
    }

    return formattedContent;
  };

  // Effect to pre-fetch lead data for any lead links in messages
  useEffect(() => {
    if (!messages || messages.length === 0) return;

    const leadIds = new Set();
    const leadUrlPattern = /(https?:\/\/[^\/]+)?\/leads\/(\d+)/g;

    // Extract lead IDs from all messages
    messages.forEach(message => {
      if (!message.content) return;
      
      let match;
      while ((match = leadUrlPattern.exec(message.content)) !== null) {
        const leadId = match[2];
        if (leadId && !leadCache[leadId]) {
          leadIds.add(leadId);
        }
      }
    });

    // Fetch lead data for all found lead IDs
    leadIds.forEach(leadId => {
      fetchLeadData(leadId);
    });
  }, [messages]);
  
  // Fetch common emojis
  useEffect(() => {
    const fetchCommonEmojis = async () => {
      try {
        const token = localStorage.getItem('token');
        const config = { headers: { 'x-auth-token': token } };
        const response = await axios.get('/api/chat/emojis', config);
        if (response.data.emojis) {
          setCommonEmojis(response.data.emojis);
        }
      } catch (error) {
        console.error('Error fetching common emojis:', error);
      }
    };
    
    fetchCommonEmojis();
  }, []);
  
  // Handle click outside emoji picker and message menu
  useEffect(() => {
    const handleClickOutside = (event) => {
      // Close emoji picker if clicked outside
      if (
        emojiPickerRef.current && 
        !emojiPickerRef.current.contains(event.target) &&
        !event.target.closest('[data-emoji-toggle]')
      ) {
        setShowEmojiPicker(null);
      }
      
      // Close message menu if clicked outside
      if (
        messageMenuRef.current && 
        !messageMenuRef.current.contains(event.target) &&
        !event.target.closest('[data-message-menu]')
      ) {
        setShowMessageMenu(null);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);
  
  // Toggle emoji picker for a specific message
  const toggleEmojiPicker = (messageId, event) => {
    if (showEmojiPicker === messageId) {
      setShowEmojiPicker(null);
      return;
    }
    
    // Get button position for absolute positioning
    const button = event.currentTarget;
    const rect = button.getBoundingClientRect();
    
    // Set CSS variables for the position
    document.documentElement.style.setProperty('--message-emoji-trigger-top', `${rect.top}px`);
    document.documentElement.style.setProperty('--message-emoji-trigger-left', `${rect.left}px`);
    document.documentElement.style.setProperty('--message-emoji-trigger-right', `${window.innerWidth - rect.right}px`);
    
    setShowEmojiPicker(messageId);
  };
  
  // Toggle message menu for a specific message
  const toggleMessageMenu = (messageId) => {
    setShowMessageMenu(prev => prev === messageId ? null : messageId);
  };
  
  // Handle emoji selection from the picker
  const handleEmojiSelect = (messageId, emoji) => {
    if (onAddReaction) {
      onAddReaction(messageId, emoji);
    }
    setShowEmojiPicker(null);
  };
  
  // Handle emoji click (toggle reaction)
  const handleEmojiClick = (messageId, emoji, hasReacted) => {
    if (hasReacted) {
      if (onRemoveReaction) {
        onRemoveReaction(messageId, emoji);
      }
    } else {
      handleEmojiSelect(messageId, emoji);
    }
  };

  return (
    <div 
      ref={ref}
      className="flex-1 overflow-y-auto p-6 flex flex-col space-y-4"
      style={{ scrollbarWidth: 'thin', scrollbarColor: '#CBD5E0 #EDF2F7' }}
    >
      {loading && messages.length === 0 ? (
        <div className="flex justify-center py-10">
          <svg className="animate-spin h-8 w-8 text-gray-400" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
        </div>
      ) : messages.length === 0 ? (
        <div className="text-center py-10">
          <p className="text-gray-500">No messages yet. Start a conversation!</p>
        </div>
      ) : (
        messages.map(message => (
          <div 
            key={message.id} 
            className={`flex flex-col relative ${message.user_id === user?.id ? 'items-end' : 'items-start'}`}
          >
            <div 
              className={`max-w-[70%] rounded-lg p-3 ${
                message.user_id === user?.id 
                  ? 'bg-blue-500 text-white' 
                  : message.user_id === -1
                    ? 'bg-amber-50 text-gray-800 border border-amber-200'
                    : 'bg-gray-100 text-gray-800'
              }`}
            >
              <div className="flex justify-between items-start">
                <span className={`font-medium text-sm ${
                  message.user_id === user?.id ? 'text-blue-50' : 'text-gray-600'
                }`}>
                  {message.user_id === -1 ? 'System' : message.user_name}
                </span>
                <div className="flex items-center space-x-1">
                  <span className={`text-xs ${
                    message.user_id === user?.id ? 'text-blue-100' : 'text-gray-500'
                  }`}>
                    {formatTimestamp(message.created_at)}
                  </span>
                  
                  {/* Emoji reaction button */}
                  <button
                    data-emoji-toggle="true"
                    onClick={(e) => toggleEmojiPicker(message.id, e)}
                    className={`text-xs p-1 rounded-full ${
                      message.user_id === user?.id 
                        ? 'text-blue-100 hover:text-white hover:bg-blue-600' 
                        : 'text-gray-400 hover:text-gray-600 hover:bg-gray-200'
                    }`}
                    title="Add reaction"
                  >
                    <div className="relative inline-flex">
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM7 9a1 1 0 100-2 1 1 0 000 2zm7-1a1 1 0 11-2 0 1 1 0 012 0zm-.464 5.535a1 1 0 10-1.415-1.414 3 3 0 01-4.242 0 1 1 0 00-1.415 1.414 5 5 0 007.072 0z" clipRule="evenodd" />
                      </svg>
                      <div className={`absolute -top-1 -right-1 ${
                        message.user_id === user?.id 
                          ? 'bg-blue-700 text-white' 
                          : 'bg-white text-gray-700'
                        } rounded-full w-2.5 h-2.5 flex items-center justify-center border border-current shadow-sm`}>
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-2 w-2" viewBox="0 0 20 20" fill="currentColor">
                          <path fillRule="evenodd" d="M10 5a1 1 0 011 1v3h3a1 1 0 110 2h-3v3a1 1 0 11-2 0v-3H6a1 1 0 110-2h3V6a1 1 0 011-1z" clipRule="evenodd" />
                        </svg>
                      </div>
                    </div>
                  </button>
                  
                  {/* Message menu (3-dot) button - only for messages user can delete */}
                  {(message.user_id === user?.id || user?.role === 'admin' || user?.role === 'superuser') && (
                    <button
                      data-message-menu="true"
                      onClick={() => toggleMessageMenu(message.id)}
                      className={`text-xs p-1 rounded-full ${
                        message.user_id === user?.id 
                          ? 'text-blue-100 hover:text-white hover:bg-blue-600' 
                          : 'text-gray-400 hover:text-gray-600 hover:bg-gray-200'
                      }`}
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                        <path d="M10 6a2 2 0 110-4 2 2 0 010 4zM10 12a2 2 0 110-4 2 2 0 010 4zM10 18a2 2 0 110-4 2 2 0 010 4z" />
                      </svg>
                    </button>
                  )}
                </div>
              </div>
              <div className="mt-1 text-sm whitespace-pre-wrap">
                {formatMessageContent(message.content)}
              </div>
              
              {/* Message dropdown menu */}
              {showMessageMenu === message.id && (
                <div 
                  ref={messageMenuRef}
                  className="absolute z-30 mt-1 bg-white rounded-md shadow-lg border border-gray-200 py-1 w-36"
                  style={{
                    [message.user_id === user?.id ? 'right' : 'left']: '0',
                    top: '40px'
                  }}
                >
                  {/* Delete option */}
                  <button
                    onClick={() => {
                      onDeleteMessage(message.id);
                      setShowMessageMenu(null); // Close menu after action
                    }}
                    className="w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-gray-100 flex items-center"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-2" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z" clipRule="evenodd" />
                    </svg>
                    Delete Message
                  </button>
                </div>
              )}
              
              <div className="mt-2">
                {/* Emoji picker */}
                {showEmojiPicker === message.id && (
                    <div 
                      ref={emojiPickerRef}
                      className="fixed z-50 bg-white rounded-lg shadow-lg border border-gray-200 p-2 overflow-visible"
                      style={{
                        transform: 'translateY(-100%)',
                        top: 'calc(var(--message-emoji-trigger-top) - 16px)',
                        left: message.user_id === user?.id ? 'auto' : 'var(--message-emoji-trigger-left)',
                        right: message.user_id === user?.id ? 'var(--message-emoji-trigger-right)' : 'auto',
                        width: '200px'
                      }}
                    >
                      {/* Arrow pointing down */}
                      <div 
                        className="absolute w-4 h-4 transform rotate-45 bg-white border-r border-b border-gray-200"
                        style={{
                          bottom: '-8px',
                          [message.user_id === user?.id ? 'right' : 'left']: '10px',
                        }}
                      ></div>
                      <div className="grid grid-cols-4 gap-2">
                        {commonEmojis.map(emoji => (
                          <button
                            key={emoji}
                            onClick={() => handleEmojiSelect(message.id, emoji)}
                            className="text-xl hover:bg-gray-100 rounded p-1"
                          >
                            {emoji}
                          </button>
                        ))}
                      </div>
                      <div className="text-xs text-gray-500 mt-2 text-center">
                        Click emoji to add
                      </div>
                    </div>
                  )}
                </div>
              </div>
            
            {/* Emoji reactions display */}
            {message.reactions && message.reactions.length > 0 && (
              <div className={`flex flex-wrap gap-1 mt-1 max-w-[70%] ${
                message.user_id === user?.id ? 'justify-end' : 'justify-start'
              }`}>
                {message.reactions.map((reaction) => {
                  const hasUserReacted = reaction.users.some(u => u.id === user?.id);
                  return (
                    <button
                      key={reaction.emoji}
                      onClick={() => handleEmojiClick(message.id, reaction.emoji, hasUserReacted)}
                      className={`flex items-center px-2 py-1 rounded-full text-xs ${
                        hasUserReacted 
                          ? 'bg-blue-100 text-blue-800 border border-blue-300' 
                          : 'bg-gray-100 text-gray-800 border border-gray-200 hover:bg-gray-200'
                      }`}
                      title={reaction.users.map(u => u.name).join(', ')}
                    >
                      <span className="mr-1">{reaction.emoji}</span>
                      <span>{reaction.count}</span>
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        ))
      )}
      <div ref={messagesEndRef} />
    </div>
  );
});

export default MessageList;